import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Point } from '@/types/floorPlanViewer';
import { useRoomsStandardized } from '@/hooks/entities/useRoomsStandardized';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { useFixturesStandardized } from '@/hooks/entities/useFixturesStandardized';
import { useControlsStandardized } from '@/hooks/entities/useControlsStandardized';
import { queryKeys } from '@/lib/queryKeys';
import { logger } from '@/lib/logger';
import { lvProductService } from '@/features/product-library/services/lvProductService';
import { useAppStore } from '@/stores/appStore';

interface RoomCreationOverlayProps {
  floorPlanId: string;
  isActive: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  zoom: number;
  position: { x: number; y: number };
  onComplete: () => void;
  imageSize: { width: number; height: number };
}

export function RoomCreationOverlay({
  floorPlanId,
  isActive,
  containerRef,
  zoom,
  position,
  onComplete,
  imageSize,
}: RoomCreationOverlayProps) {
  const [points, setPoints] = useState<Point[]>([]);
  const [previewPoint, setPreviewPoint] = useState<Point | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const { createRoom } = useRoomsStandardized(floorPlanId);
  const queryClient = useQueryClient();

  // Get floor plan data to access fixtures and controls
  const { data } = useFloorPlanContext();
  const { updateFixture } = useFixturesStandardized(floorPlanId);
  const { updateControl } = useControlsStandardized(floorPlanId);

  // Get room confidence threshold from app store
  const roomConfidenceThreshold = useAppStore(state => state.aiTools.roomConfidenceThreshold);

  // Track last click time to prevent double clicks
  const lastClickTimeRef = useRef<number>(0);
  const CLICK_DEBOUNCE_MS = 100;

  // Convert screen coordinates to image coordinates
  const screenToImageCoords = useCallback(
    (clientX: number, clientY: number): Point | null => {
      if (!containerRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      // Calculate position relative to the viewport
      const x = (clientX - rect.left - position.x) / zoom;
      const y = (clientY - rect.top - position.y) / zoom;

      return { x, y };
    },
    [containerRef, position, zoom],
  );

  // Calculate distance between two points
  const distance = (p1: Point, p2: Point) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  // Check if a point is inside a polygon using ray casting algorithm
  const isPointInPolygon = (point: Point, polygon: Point[]): boolean => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x,
        yi = polygon[i].y;
      const xj = polygon[j].x,
        yj = polygon[j].y;

      const intersect =
        yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Complete room creation
  const handleComplete = useCallback(async () => {
    if (points.length < 3) {
      toast.error('A room must have at least 3 points');
      return;
    }

    try {
      logger.log('Creating room with points:', points);

      // Calculate bounding box for the room
      const xValues = points.map(p => p.x);
      const yValues = points.map(p => p.y);

      const minX = Math.min(...xValues);
      const maxX = Math.max(...xValues);
      const minY = Math.min(...yValues);
      const maxY = Math.max(...yValues);

      const width = maxX - minX;
      const height = maxY - minY;
      const x_position = minX + width / 2;
      const y_position = minY + height / 2;

      const roomData = {
        floor_plan_id: floorPlanId,
        points: points,
        name: `Room`,
        confidence: 1.0,
        x_position,
        y_position,
        width,
        height,
      };

      logger.log('Creating room with data:', roomData);

      const result = await createRoom(roomData, { showSuccessToast: false });

      logger.log('Room creation result:', result);

      // Check if we successfully created a room and have a room ID
      if (result && result.id) {
        logger.log('Floor plan data:', data);
        logger.log('Fixtures from context:', data.fixtures);
        logger.log('Controls from context:', data.controls);

        // Find all fixtures, controls, and products that are inside the newly created room
        const fixturesToUpdate: string[] = [];
        const controlsToUpdate: string[] = [];
        const productsToUpdate: string[] = [];

        // Check fixtures
        logger.log('Checking fixtures. Total fixtures:', data.fixtures?.length || 0);
        logger.log('Room polygon points:', points);

        if (data.fixtures && data.fixtures.length > 0) {
          data.fixtures.forEach(fixture => {
            const fixturePoint = { x: fixture.x_position, y: fixture.y_position };
            const isInside = isPointInPolygon(fixturePoint, points);
            logger.log(
              `Fixture ${fixture.id} at (${fixturePoint.x}, ${fixturePoint.y}) - inside: ${isInside}, has room: ${!!fixture.room_id}`,
            );

            if (isInside) {
              // Only update fixtures that don't already have a room assigned
              if (!fixture.room_id) {
                fixturesToUpdate.push(fixture.id);
              }
            }
          });
        }

        // Check controls
        logger.log('Checking controls. Total controls:', data.controls?.length || 0);

        if (data.controls && data.controls.length > 0) {
          data.controls.forEach(control => {
            // Controls use xPosition and yPosition from the API
            const controlPoint = {
              x: control.xPosition || control.x,
              y: control.yPosition || control.y,
            };
            const isInside = isPointInPolygon(controlPoint, points);
            logger.log(
              `Control ${control.id} at (${controlPoint.x}, ${controlPoint.y}) - inside: ${isInside}, has room: ${!!(control.roomId || control.room_id)}`,
            );

            if (isInside) {
              // Only update controls that don't already have a room assigned
              // Check both roomId (from typed interface) and room_id (from database)
              if (!control.roomId && !control.room_id) {
                controlsToUpdate.push(control.id);
              }
            }
          });
        }

        // Check products
        logger.log('Checking products. Total products:', data.lvProducts?.length || 0);

        if (data.lvProducts && data.lvProducts.length > 0) {
          data.lvProducts.forEach(product => {
            const productPoint = { x: product.x_position, y: product.y_position };
            const isInside = isPointInPolygon(productPoint, points);
            logger.log(
              `Product ${product.id} at (${productPoint.x}, ${productPoint.y}) - inside: ${isInside}, has room: ${!!product.room_id}`,
            );

            if (isInside) {
              // Only update products that don't already have a room assigned
              if (!product.room_id) {
                productsToUpdate.push(product.id);
              }
            }
          });
        }

        // Update fixtures with the new room ID
        if (fixturesToUpdate.length > 0) {
          logger.log(`Updating ${fixturesToUpdate.length} fixtures with room ${result.id}`);
          const updatePromises = fixturesToUpdate.map(fixtureId =>
            updateFixture(fixtureId, { room_id: result.id }, { showSuccessToast: false }),
          );

          try {
            await Promise.all(updatePromises);
            logger.log('Fixtures updated successfully');
          } catch (error) {
            logger.error('Error updating fixtures with room:', error);
          }
        }

        // Update controls with the new room ID
        if (controlsToUpdate.length > 0) {
          logger.log(`Updating ${controlsToUpdate.length} controls with room ${result.id}`);
          const updatePromises = controlsToUpdate.map(controlId =>
            updateControl(controlId, { room_id: result.id }, { showSuccessToast: false }),
          );

          try {
            await Promise.all(updatePromises);
            logger.log('Controls updated successfully');
          } catch (error) {
            logger.error('Error updating controls with room:', error);
          }
        }

        // Update products with the new room ID
        if (productsToUpdate.length > 0) {
          logger.log(`Updating ${productsToUpdate.length} products with room ${result.id}`);
          const productIds = new Set(productsToUpdate);
          queryClient.setQueryData(
            [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
            (oldProducts: any[] | undefined) =>
              Array.isArray(oldProducts)
                ? oldProducts.map(product =>
                    productIds.has(product.id)
                      ? { ...product, room_id: result.id, updated_at: new Date().toISOString() }
                      : product,
                  )
                : oldProducts,
          );

          const updatePromises = productsToUpdate.map(productId =>
            lvProductService.update(productId, { room_id: result.id }),
          );

          try {
            await Promise.all(updatePromises);
            logger.log('Products updated successfully');
          } catch (error) {
            logger.error('Error updating products with room:', error);
          }
        }

        const totalUpdated =
          fixturesToUpdate.length + controlsToUpdate.length + productsToUpdate.length;
        logger.log(`Room created silently; assigned ${totalUpdated} enclosed elements`);

        // Keep active UI optimistic and let inactive views catch up quietly.
        void Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: queryKeys.rooms(floorPlanId),
            refetchType: 'inactive',
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.fixtures(floorPlanId),
            refetchType: 'inactive',
          }),
          queryClient.invalidateQueries({
            queryKey: queryKeys.controls(floorPlanId),
            refetchType: 'inactive',
          }),
          queryClient.invalidateQueries({
            queryKey: [...queryKeys.floorPlan(floorPlanId), 'lv-products'],
            refetchType: 'inactive',
          }),
        ]);
      }

      setPoints([]);
      setIsDrawing(false);
      setPreviewPoint(null);
      onComplete();
    } catch (error) {
      logger.error('Failed to create room:', error);
      toast.error('Failed to create room');
    }
  }, [
    points,
    floorPlanId,
    createRoom,
    queryClient,
    onComplete,
    data,
    updateFixture,
    updateControl,
    isPointInPolygon,
  ]);

  // Handle mouse/touch down
  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isActive || !containerRef.current) return;

      // Check for multi-touch and bail out if detected
      if ('touches' in e && e.touches.length > 1) {
        return; // Don't handle room creation during multi-touch
      }

      // Debounce rapid clicks to prevent double room creation
      const now = Date.now();
      if (now - lastClickTimeRef.current < CLICK_DEBOUNCE_MS) {
        return; // Ignore rapid successive clicks
      }
      lastClickTimeRef.current = now;

      // Only prevent default for mouse events (non-passive)
      // Touch events use CSS touch-action: none instead
      if (!('touches' in e)) {
        e.preventDefault();
      }
      e.stopPropagation();

      // Get coordinates from mouse or touch event
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      let point = screenToImageCoords(clientX, clientY);
      if (!point) return;

      // Apply shift key constraint when adding points (except for the first point)
      if (e.shiftKey && points.length > 0) {
        const lastPoint = points[points.length - 1];
        const dx = Math.abs(point.x - lastPoint.x);
        const dy = Math.abs(point.y - lastPoint.y);

        if (dx > dy) {
          // Horizontal constraint - keep y the same as last point
          point = { x: point.x, y: lastPoint.y };
        } else {
          // Vertical constraint - keep x the same as last point
          point = { x: lastPoint.x, y: point.y };
        }
      }

      logger.log('Mouse down at:', point, 'Current points:', points.length);

      // Check if we're closing the polygon (clicking near the first point)
      if (points.length >= 3 && distance(point, points[0]) < 20) {
        // Complete the room
        handleComplete();
        return;
      }

      // Check if the point is inside any visible room (filtered by confidence threshold)
      const existingRooms = data.rooms || [];
      const visibleRooms = existingRooms.filter(room => {
        const confidence = room.confidence ?? 1;
        return confidence >= roomConfidenceThreshold;
      });

      for (const room of visibleRooms) {
        if (room.points && isPointInPolygon(point, room.points)) {
          toast.error('Cannot place point inside a visible room');
          return;
        }
      }

      // Add new point
      setPoints(prev => [...prev, point]);
      setIsDrawing(true);
    },
    [
      isActive,
      containerRef,
      points,
      screenToImageCoords,
      handleComplete,
      data.rooms,
      isPointInPolygon,
    ],
  );

  // Handle mouse/touch move (for preview line)
  const handlePointerMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isActive || points.length === 0) {
        setPreviewPoint(null);
        return;
      }

      // Check for multi-touch and bail out if detected
      if ('touches' in e && e.touches.length > 1) {
        setPreviewPoint(null);
        return;
      }

      // Get coordinates from mouse or touch event
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const shiftKey = 'shiftKey' in e ? e.shiftKey : false;

      const point = screenToImageCoords(clientX, clientY);
      if (point) {
        // Apply shift key constraint for horizontal/vertical lines
        if (shiftKey && points.length > 0) {
          const lastPoint = points[points.length - 1];
          const dx = Math.abs(point.x - lastPoint.x);
          const dy = Math.abs(point.y - lastPoint.y);

          if (dx > dy) {
            // Horizontal constraint - keep y the same as last point
            setPreviewPoint({ x: point.x, y: lastPoint.y });
          } else {
            // Vertical constraint - keep x the same as last point
            setPreviewPoint({ x: lastPoint.x, y: point.y });
          }
        } else {
          setPreviewPoint(point);
        }
      }
    },
    [isActive, points, screenToImageCoords],
  );

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDrawing) {
        setPoints([]);
        setIsDrawing(false);
        setPreviewPoint(null);
        onComplete();
      }
    },
    [isDrawing, onComplete],
  );

  // Event listeners
  useEffect(() => {
    if (isActive) {
      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('touchmove', handlePointerMove, { passive: false });
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.removeEventListener('touchmove', handlePointerMove);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isActive, handlePointerMove, handleKeyDown]);

  // Reset when inactive
  useEffect(() => {
    if (!isActive) {
      setPoints([]);
      setIsDrawing(false);
      setPreviewPoint(null);
    }
  }, [isActive]);

  if (!isActive) return null;

  // Create path data
  const pathData =
    points.length > 0
      ? `M ${points[0].x} ${points[0].y} ${points
          .slice(1)
          .map(p => `L ${p.x} ${p.y}`)
          .join(' ')}`
      : '';

  // Preview line
  const previewLine =
    points.length > 0 && previewPoint
      ? `M ${points[points.length - 1].x} ${points[points.length - 1].y} L ${previewPoint.x} ${previewPoint.y}`
      : '';

  return (
    <>
      {/* Overlay for capturing clicks */}
      <div
        className="absolute inset-0 z-30"
        style={{
          cursor: 'crosshair',
          touchAction: 'none', // Prevent touch defaults via CSS
        }}
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      />

      {/* SVG for drawing the room - rendered inside the transformed container */}
      <div
        className="absolute inset-0 pointer-events-none z-40"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
      >
        <svg
          className="absolute"
          style={{
            // Make the SVG much larger than the image to allow drawing outside
            width: (imageSize.width || 1000) * 3,
            height: (imageSize.height || 1000) * 3,
            left: -(imageSize.width || 1000),
            top: -(imageSize.height || 1000),
          }}
          viewBox={`${-(imageSize.width || 1000)} ${-(imageSize.height || 1000)} ${(imageSize.width || 1000) * 3} ${(imageSize.height || 1000) * 3}`}
        >
          {/* Room being drawn */}
          {points.length > 0 && (
            <>
              {/* Fill for polygon if we have more than 2 points */}
              {points.length > 2 && (
                <path
                  d={`${pathData} Z`}
                  fill="rgba(0, 120, 255, 0.2)"
                  stroke="none"
                  pointerEvents="none"
                />
              )}

              {/* Stroke for all connected points */}
              <path
                d={pathData}
                fill="none"
                stroke="rgb(0, 100, 255)"
                strokeWidth={2 / zoom}
                pointerEvents="none"
              />

              {/* Preview line to next point */}
              {previewLine && (
                <path
                  d={previewLine}
                  fill="none"
                  stroke="rgb(0, 100, 255)"
                  strokeWidth={2 / zoom}
                  strokeDasharray={`${5 / zoom} ${3 / zoom}`}
                  pointerEvents="none"
                />
              )}

              {/* Close polygon indicator when near first point */}
              {points.length >= 3 && previewPoint && distance(previewPoint, points[0]) < 20 && (
                <circle
                  cx={points[0].x}
                  cy={points[0].y}
                  r={10 / zoom}
                  fill="rgba(0, 255, 0, 0.3)"
                  stroke="rgb(0, 200, 0)"
                  strokeWidth={2 / zoom}
                  pointerEvents="none"
                />
              )}

              {/* Points */}
              {points.map((point, index) => (
                <circle
                  key={index}
                  cx={point.x}
                  cy={point.y}
                  r={5 / zoom}
                  fill="white"
                  stroke="rgb(0, 100, 255)"
                  strokeWidth={2 / zoom}
                  pointerEvents="none"
                />
              ))}
            </>
          )}
        </svg>
      </div>

      {/* Instructions */}
      <div
        className="absolute bottom-[env(safe-area-inset-bottom,16px)] left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded shadow-lg pointer-events-none z-50"
        style={{ marginBottom: 'max(16px, env(safe-area-inset-bottom))' }}
      >
        <p className="text-sm font-medium">
          {points.length === 0
            ? 'Click to start drawing a room'
            : points.length < 3
              ? `Click to add point ${points.length + 1} (min 3 points)`
              : 'Click near first point to close, or continue adding points'}
        </p>
        <p className="text-xs opacity-80">Hold SHIFT for straight lines • Press ESC to cancel</p>
      </div>
    </>
  );
}
